import { PaywallFreeBgJpg } from '@/assets/image/free-to-pay/paywall-free-bg-jpg';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import { IS_HIDE_PRO_MONTHLY_OPTION } from '@/features/flags/legacy-flags';
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import {
  getDisplayedPriceFromPlan,
  getPlanFromPeriod,
  getTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import useReverseTrial from '@/hooks/useReverseTrial';
import {
  trialToPaidModalClosed,
  trialToPaidModalPeriodChanged,
  trialToPaidModalUpgradeClicked,
} from '@/lib/analytics/scribe/ui';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { getStripeCallbackRedirect, getStripeReturnRedirect } from '@/utils/scribeHTTP';
import { useTranslations } from '@/utils/translations';
import { Period } from '@heidi/commerce/contracts';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierProFilled } from '@heidi/design-system/icons';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerHeader,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyH2,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { Gauge, NotebookPen, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { PaywallDesktopBranding } from './paywall-desktop-branding';
import { PaywallOverlayTrial } from './paywall-overlay-trial';

export const TrialToPaidModal = () => {
  const { trialToPaidModalOpen, toggleTrialToPaidModal } = usePaywallStates();
  const {
    trialDaysLeft,
    trialHoursLeft,
    showReverseModal,
    reverseTrialTitle,
    reverseTrialLostAccessText,
    isInReverseTrial,
  } = useReverseTrial();
  const { data: products } = useQueryProducts();
  const product = getTierProduct(SubscriptionTier.PRO, products);
  const [period, setPeriod] = useState<Period>(Period.ONE_YEAR);

  const { mutate: createCheckoutSession, isPending: isCreatingCheckout } =
    useMutateCheckoutV2();
  const { t, locale } = useTranslations();

  if (isAppleCompliantBuild()) {
    return null;
  }

  const onStayProAfterTrial = () => {
    trialToPaidModalUpgradeClicked({
      period: period,
    });
    createCheckoutSession({
      plan_period: period,
      tier: SubscriptionTier.PRO,
      fe_endpoint: getStripeCallbackRedirect(isInReverseTrial),
      fe_return_endpoint: getStripeReturnRedirect(),
      free_trial_days: trialDaysLeft,
    });
  };

  return (
    <>
      {showReverseModal && (
        <PaywallOverlayTrial trialToPaidModalOpen={trialToPaidModalOpen} />
      )}
      <DialogDrawer
        modal
        dismissible={!showReverseModal}
        open={trialToPaidModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            trialToPaidModalClosed();
          }
          toggleTrialToPaidModal(open);
        }}
      >
        <DialogDrawerContent
          disableDrag={showReverseModal}
          hideCloseIcon
          overlayClassName="backdrop-blur-[3px]"
          className={cn(
            'flex max-h-[90dvh] gap-2 overflow-hidden border-none sm:w-11/12',
            showReverseModal
              ? 'bg-transparent p-0 sm:max-w-2xl md:max-w-3xl lg:max-w-4xl'
              : 'mb-auto h-fit max-h-fit flex-col gap-4 rounded-md bg-surface p-4 sm:w-[615px] sm:p-10'
          )}
        >
          {showReverseModal && (
            <PaywallFreeBgJpg
              alt=""
              fill
              priority
              className="absolute left-0 top-0 z-[-1] hidden object-cover sm:block"
            />
          )}
          {showReverseModal && <PaywallDesktopBranding />}

          <div
            className={cn(
              'flex w-full flex-col',
              showReverseModal
                ? 'flex flex-col gap-4 rounded-md bg-surface p-4 sm:m-4 sm:max-h-full sm:w-1/2 sm:p-8'
                : 'gap-4'
            )}
          >
            <DialogDrawerHeader
              className={cn(
                'flex flex-col gap-4',
                showReverseModal ? 'items-start' : 'items-center'
              )}
            >
              {!showReverseModal && <FramedIcon state="pro" />}
              <div
                className={cn(
                  'flex w-full flex-col',
                  showReverseModal ? 'items-start' : 'items-center'
                )}
              >
                <TypographyH2
                  className={cn(
                    showReverseModal ? 'text-left' : 'text-center',
                    !showReverseModal
                      ? trialHoursLeft <= 1
                        ? 'px-6 sm:px-4'
                        : 'px-2 sm:px-12'
                      : null,
                    'text-2xl sm:text-3xl'
                  )}
                >
                  {reverseTrialTitle}
                </TypographyH2>
                <TypographyP2Bold
                  className={cn(
                    'text-text-secondary',
                    showReverseModal ? 'text-left' : 'text-center'
                  )}
                >
                  {t('trialToPaidModal.downgradeWarning')}
                </TypographyP2Bold>
              </div>
            </DialogDrawerHeader>

            <SegmentedControl
              value={period}
              className="w-full gap-4"
              onValueChange={(value: string) => {
                const period = value as Period;
                if (period === Period.ONE_YEAR || period === Period.ONE_MONTH) {
                  setPeriod(period);
                  trialToPaidModalPeriodChanged({
                    period: period,
                  });
                }
              }}
            >
              {!IS_HIDE_PRO_MONTHLY_OPTION() && (
                <SegmentedControlList className="w-full">
                  <SegmentedControlTrigger value={Period.ONE_YEAR}>
                    {t('trialToPaidModal.yearly')}
                    <Badge color="green" size="xs">
                      {t('trialToPaidModal.savePercent')}
                    </Badge>
                  </SegmentedControlTrigger>
                  <SegmentedControlTrigger value={Period.ONE_MONTH}>
                    {t('trialToPaidModal.monthly')}
                  </SegmentedControlTrigger>
                </SegmentedControlList>
              )}
              <section>
                <div className="flex flex-col gap-5 rounded-md border border-border bg-surface-page p-4">
                  <section className="flex items-start gap-2">
                    <TypographyH2 className="p-1 leading-none">
                      {getDisplayedPriceFromPlan(
                        getPlanFromPeriod(period, product),
                        'month',
                        false,
                        locale
                      )}
                    </TypographyH2>
                    <div className="flex flex-col text-text-secondary">
                      <TypographyCaption>
                        {t('trialToPaidModal.perMonth')}
                      </TypographyCaption>
                      <TypographyCaption>
                        {period === Period.ONE_YEAR
                          ? t('trialToPaidModal.billedYearly')
                          : t('trialToPaidModal.billedMonthly')}
                      </TypographyCaption>
                    </div>
                  </section>

                  <section className="flex flex-col gap-2">
                    <TypographyP2Bold>{reverseTrialLostAccessText}</TypographyP2Bold>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <NotebookPen
                          className="text-tier-pro-text"
                          width={24}
                          height={24}
                        />
                        <div>
                          <TypographyP2>
                            {t('trialToPaidModal.unlimitedCustomTemplates')}
                          </TypographyP2>
                          <TypographyCaption className="text-text-secondary">
                            {t('trialToPaidModal.unlimitedCustomTemplatesDesc')}
                          </TypographyCaption>
                        </div>
                      </li>
                      <li className="flex items-center gap-2">
                        <Sparkles className="text-tier-pro-text" width={24} height={24} />
                        <div>
                          <TypographyP2>
                            {t('trialToPaidModal.unlimitedAskHeidiPrompts')}
                          </TypographyP2>
                          <TypographyCaption className="text-text-secondary">
                            {t('trialToPaidModal.unlimitedAskHeidiPromptsDesc')}
                          </TypographyCaption>
                        </div>
                      </li>
                      <li className="flex items-center gap-2">
                        <Gauge className="text-tier-pro-text" width={24} height={24} />
                        <div>
                          <TypographyP2>
                            {t('trialToPaidModal.lightSpeedProductivity')}
                          </TypographyP2>
                          <TypographyCaption className="text-text-secondary">
                            {t('trialToPaidModal.lightSpeedProductivityDesc')}
                          </TypographyCaption>
                        </div>
                      </li>
                    </ul>
                  </section>
                </div>
              </section>
            </SegmentedControl>
            {!isAppleCompliantBuild() && (
              <Button
                size="lg"
                className="w-full"
                variant="pro"
                disabled={isCreatingCheckout}
                onClick={onStayProAfterTrial}
              >
                <TierProFilled size={16} />
                {isCreatingCheckout
                  ? t('trialToPaidModal.redirecting')
                  : t('trialToPaidModal.stayOnProButton')}
              </Button>
            )}
            <TypographyFootNote className="px-0 text-center text-text-secondary sm:px-4">
              {t('trialToPaidModal.footerText')}
            </TypographyFootNote>
          </div>
        </DialogDrawerContent>
      </DialogDrawer>
    </>
  );
};
